import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../../services/api';
import { useIsMobile } from '../../hooks/useResponsive';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';

const V  = '#8B5CF6';
const VD = '#6D28D9';

const STATUS = { pending: 'En attente', confirmed: 'Confirmé', processing: 'Préparation', shipped: 'Expédié', delivered: 'Livré', cancelled: 'Annulé' };
const STATUS_COLOR = { pending: '#F59E0B', confirmed: '#10B981', processing: '#3B82F6', shipped: V, delivered: '#10B981', cancelled: '#EF4444' };

function Sidebar({ open, onClose }) {
  const isMobile = useIsMobile();
  const links = [
    { to: '/admin',           icon: '📊', l: 'Tableau de bord' },
    { to: '/admin/produits',  icon: '🧴', l: 'Produits' },
    { to: '/admin/commandes', icon: '📦', l: 'Commandes' },
    { to: '/',                icon: '🏠', l: 'Boutique' },
  ];

  if (isMobile && !open) return null;

  return (
    <aside style={{
      width: isMobile ? '100%' : 220,
      background: VD, minHeight: isMobile ? 'auto' : '100vh',
      padding: isMobile ? '1rem 0' : '2rem 0', flexShrink: 0,
      position: isMobile ? 'fixed' : 'relative',
      top: isMobile ? 0 : 'auto', left: 0, right: 0, bottom: isMobile ? 0 : 'auto',
      zIndex: isMobile ? 500 : 'auto',
      overflowY: isMobile ? 'auto' : 'visible',
    }}>
      {isMobile && (
        <button onClick={onClose} style={{ position: 'absolute', top: 12, right: 16, background: 'none', border: 'none', color: '#fff', fontSize: 24, cursor: 'pointer' }}>✕</button>
      )}
      <div style={{ padding: '0 1.5rem 1.5rem', borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
        <p style={{ fontFamily: 'Georgia, serif', fontSize: 15, color: '#fff', letterSpacing: 3 }}>SS PARFUMERIE</p>
        <p style={{ fontSize: 9, color: 'rgba(255,255,255,0.5)', letterSpacing: 2, textTransform: 'uppercase', marginTop: 4 }}>Administration</p>
      </div>
      <nav style={{ marginTop: 12 }}>
        {links.map(({ to, icon, l }) => (
          <Link key={to} to={to} onClick={isMobile ? onClose : undefined} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 1.5rem', fontSize: 13, color: 'rgba(255,255,255,0.75)', textDecoration: 'none' }}
            onMouseEnter={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.1)'; e.currentTarget.style.color = '#fff'; }}
            onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'rgba(255,255,255,0.75)'; }}>
            <span style={{ fontSize: 16 }}>{icon}</span>{l}
          </Link>
        ))}
      </nav>
    </aside>
  );
}

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [recentOrders, setRecentOrders] = useState([]);
  const [lowStock, setLowStock] = useState([]);
  const [menuOpen, setMenuOpen] = useState(false);
  const isMobile = useIsMobile();

  useEffect(() => {
    api.get('/api/admin/stats').then(r => setStats(r.data)).catch(() => {});
    api.get('/api/admin/orders', { params: { limit: 5 } }).then(r => setRecentOrders(r.data.orders || [])).catch(() => {});
    api.get('/api/admin/products/low-stock').then(r => setLowStock(r.data || [])).catch(() => {});
  }, []);

  if (!stats) return (
    <div style={{ display: 'flex', minHeight: '100vh' }}>
      <Sidebar open={false} onClose={() => {}} />
      <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#888', fontSize: 14 }}>
        Chargement...
      </div>
    </div>
  );

  const kpis = [
    { icon: '💰', label: 'Revenu total',    value: `${(stats.revenue?.total||0).toLocaleString()} FCFA`, color: '#10B981', bg: '#D1FAE5' },
    { icon: '📅', label: 'Ce mois-ci',      value: `${(stats.revenue?.thisMonth||0).toLocaleString()} FCFA`, color: '#3B82F6', bg: '#DBEAFE' },
    { icon: '📦', label: 'Commandes',        value: stats.totalOrders||0, color: V, bg: '#EDE9FE' },
    { icon: '👥', label: 'Clients',          value: stats.totalClients||0, color: '#F59E0B', bg: '#FEF3C7' },
    { icon: '🛒', label: 'Panier moyen',     value: `${stats.averageOrder||0} FCFA`, color: '#EF4444', bg: '#FEE2E2' },
    { icon: '🆕', label: 'Nouveaux clients', value: stats.monthClients||0, color: '#8B5CF6', bg: '#EDE9FE' },
  ];

  return (
    <div style={{ display: 'flex', minHeight: '100vh', background: '#F8F7FF', color: '#111', flexDirection: isMobile ? 'column' : 'row' }}>
      {/* Overlay mobile */}
      {isMobile && menuOpen && <div onClick={() => setMenuOpen(false)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 499 }} />}
      <Sidebar open={menuOpen} onClose={() => setMenuOpen(false)} />

      <main style={{ flex: 1, padding: isMobile ? '1rem' : '2rem', overflowY: 'auto', overflowX: 'hidden' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24, flexWrap: 'wrap', gap: 12 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            {isMobile && (
              <button onClick={() => setMenuOpen(true)} style={{ background: VD, border: 'none', color: '#fff', padding: '8px 12px', borderRadius: 6, cursor: 'pointer', fontSize: 16 }}>☰</button>
            )}
            <div>
              <h1 style={{ fontFamily: 'Georgia, serif', fontSize: isMobile ? 22 : 28, fontWeight: 300, color: VD }}>Tableau de bord</h1>
              <p style={{ fontSize: 12, color: '#888', marginTop: 2 }}>{new Date().toLocaleDateString('fr-FR', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</p>
            </div>
          </div>
          <Link to="/admin/produits" style={{ background: V, color: '#fff', padding: '10px 18px', fontSize: 11, letterSpacing: 2, textTransform: 'uppercase', textDecoration: 'none', borderRadius: 6 }}>+ Produit</Link>
        </div>

        {/* KPIs */}
        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(auto-fill, minmax(175px, 1fr))', gap: 12, marginBottom: 24 }}>
          {kpis.map(k => (
            <div key={k.label} style={{ background: '#fff', border: '1px solid #EEE', borderRadius: 10, padding: '1rem', display: 'flex', gap: 12, alignItems: 'flex-start' }}>
              <div style={{ width: 40, height: 40, borderRadius: 10, background: k.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, flexShrink: 0 }}>{k.icon}</div>
              <div style={{ minWidth: 0 }}>
                <p style={{ fontSize: 9, color: '#999', letterSpacing: 1, textTransform: 'uppercase', marginBottom: 3 }}>{k.label}</p>
                <p style={{ fontFamily: 'Georgia, serif', fontSize: isMobile ? 14 : 17, color: k.color, fontWeight: 400, wordBreak: 'break-word' }}>{k.value}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Graphique + Alertes */}
        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 280px', gap: 16, marginBottom: 24 }}>
          <div style={{ background: '#fff', border: '1px solid #EEE', borderRadius: 10, padding: '1.5rem' }}>
            <p style={{ fontSize: 10, letterSpacing: 2, color: V, textTransform: 'uppercase', fontWeight: 600, marginBottom: 16 }}>Revenus — 7 derniers jours</p>
            {stats.dailySales?.length > 0 ? (
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={stats.dailySales} margin={{ top: 0, right: 0, left: -20, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#F3F3F3" />
                  <XAxis dataKey="_id" tick={{ fill: '#AAA', fontSize: 10 }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fill: '#AAA', fontSize: 10 }} axisLine={false} tickLine={false} />
                  <Tooltip contentStyle={{ background: '#fff', border: '1px solid #EDE9FE', borderRadius: 8, fontSize: 12 }} />
                  <Bar dataKey="revenue" fill={V} radius={[6, 6, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div style={{ height: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#CCC', fontSize: 13 }}>Aucune donnée</div>
            )}
          </div>

          <div style={{ background: '#fff', border: '1px solid #EEE', borderRadius: 10, padding: '1.5rem' }}>
            <p style={{ fontSize: 10, letterSpacing: 2, color: '#EF4444', textTransform: 'uppercase', fontWeight: 600, marginBottom: 14 }}>⚠️ Stock faible</p>
            {lowStock.length === 0 ? (
              <p style={{ fontSize: 13, color: '#10B981', textAlign: 'center', marginTop: 30 }}>✅ Stocks OK</p>
            ) : (
              lowStock.slice(0, 6).map(p => (
                <div key={p._id} style={{ display: 'flex', justifyContent: 'space-between', padding: '7px 0', borderBottom: '1px solid #F5F5F5', fontSize: 12 }}>
                  <span style={{ color: '#555', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.name}</span>
                  <span style={{ color: '#EF4444', fontWeight: 600, fontSize: 11, marginLeft: 8 }}>{p.variants?.[0]?.stock ?? 0}</span>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Commandes récentes */}
        <div style={{ background: '#fff', border: '1px solid #EEE', borderRadius: 10, padding: '1.5rem', overflowX: 'auto' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
            <p style={{ fontSize: 10, letterSpacing: 2, color: V, textTransform: 'uppercase', fontWeight: 600 }}>Commandes récentes</p>
            <Link to="/admin/commandes" style={{ fontSize: 12, color: V, textDecoration: 'none' }}>Voir tout →</Link>
          </div>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12, minWidth: 480 }}>
            <thead>
              <tr style={{ borderBottom: '2px solid #F5F4FF' }}>
                {['Commande', 'Client', 'Montant', 'Statut', 'Date'].map(h => (
                  <th key={h} style={{ textAlign: 'left', padding: '8px 10px', fontSize: 10, letterSpacing: 1, color: '#AAA', textTransform: 'uppercase', fontWeight: 500 }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {recentOrders.length === 0 ? (
                <tr><td colSpan={5} style={{ padding: '2rem', textAlign: 'center', color: '#CCC' }}>Aucune commande</td></tr>
              ) : recentOrders.map(o => (
                <tr key={o._id} style={{ borderBottom: '1px solid #F9F9F9' }}>
                  <td style={{ padding: '10px', fontFamily: 'Georgia, serif', color: V }}>{o.orderNumber}</td>
                  <td style={{ padding: '10px', color: '#555' }}>{o.user?.name || '—'}</td>
                  <td style={{ padding: '10px', fontWeight: 600 }}>{o.total} FCFA</td>
                  <td style={{ padding: '10px' }}>
                    <span style={{ fontSize: 10, background: (STATUS_COLOR[o.status]||'#888')+'20', color: STATUS_COLOR[o.status]||'#888', padding: '2px 8px', borderRadius: 20, fontWeight: 500 }}>
                      {STATUS[o.status]||o.status}
                    </span>
                  </td>
                  <td style={{ padding: '10px', color: '#AAA', fontSize: 11 }}>{new Date(o.createdAt).toLocaleDateString('fr-FR')}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </main>
    </div>
  );
}
